/**
 * Chart-side state of the Vest TP / SL drag (#639): which leg is being
 * dragged, which +TP / +SL draft lines exist, which requests are in flight.
 *
 * The positions push (vestPositionsStore) stays the truth. An override here
 * only bridges the gap between a drop and the push that shows its result:
 * - a refused drop (wrong side) never becomes an override: the line snaps back;
 * - a failed request drops its override at once (snap back) and toasts why;
 * - a successful one keeps the optimistic price until the next push for that
 *   broker (or CONFIRM_HOLD_MS, whichever is first), then the row decides.
 * While a leg's request is in flight its line ignores further drags.
 *
 * Commands are built by utils/vestTpslDrag.ts and are the overlay's own
 * (`broker:replace-bracket`, `broker:cancel-order`). Nothing else is sent.
 */

import { create } from 'zustand';
import { addToast } from './toastStore';
import { wsService } from '../services/WebSocketService';
import { useVestPositionsStore } from './vestPositionsStore';
import type { VestLegOverride, VestPositionRow } from '../utils/vestPositionLines';
import {
  buildVestRemoveLegCommand,
  buildVestSetLegCommand,
  checkVestLegPrice,
  matchVestReply,
  snapVestPrice,
  vestLegKey,
  VEST_BRACKET_REPLACED,
  VEST_BROKER_ERROR,
  VEST_ORDER_CANCELLED,
  type VestLegKind,
  type VestPendingRequest,
  type VestTpslCommand,
} from '../utils/vestTpslDrag';

/** No reply by then: snap back and say so (the service's own replace timeout is 2 x 10 s). */
export const VEST_TPSL_REPLY_TIMEOUT_MS = 25_000;
/** After a successful reply, keep the optimistic line at most this long waiting for a push. */
export const VEST_TPSL_CONFIRM_HOLD_MS = 8_000;

export interface VestLegEntry extends VestLegOverride {
  /** Set while a request is in flight or confirmed-awaiting-push. */
  request?: VestPendingRequest;
  /** The reply came back OK; dropped on the next positions push. */
  confirmed?: boolean;
  /** For `dragging`: the draft price to fall back to when the drag is refused. */
  draftPrice?: number;
}

interface VestTpslState {
  legs: Readonly<Record<string, VestLegEntry>>;
  /** Pointer moved a leg: show it at `price` (already on the tick). Ignored while that leg is in flight. */
  dragTo: (row: VestPositionRow, kind: VestLegKind, price: number) => void;
  /** Drag released without moving: drop the drag state. */
  cancelDrag: (row: VestPositionRow, kind: VestLegKind) => void;
  /** +TP / +SL: a draft line at `price`, NOT sent. */
  addDraft: (row: VestPositionRow, kind: VestLegKind, price: number) => void;
  discardDraft: (row: VestPositionRow, kind: VestLegKind) => void;
  /** Drop a leg (moved or draft) at `price`: validate, then send. */
  drop: (row: VestPositionRow, kind: VestLegKind, price: number, ctx: { mark: number | null; tick: number | null; decimals: number }) => void;
  /** The x on a working leg: cancel it through the overlay's remove path. */
  remove: (row: VestPositionRow, kind: VestLegKind) => void;
  /** Apply a reply event (bracket-replaced / order-cancelled / error). */
  handleReply: (event: string, data: unknown) => void;
  /**
   * A positions push for `brokerId` arrived: confirmed overrides give way to
   * the row, and every leg of a market that is no longer open goes (drafts too).
   */
  onPositionsPush: (brokerId: string, rows: readonly VestPositionRow[]) => void;
  reset: () => void;
}

/** Injected for tests: how a command leaves, the clock, timers. */
export interface VestTpslDeps {
  send: (cmd: VestTpslCommand) => boolean;
  now: () => number;
  setTimer: (fn: () => void, ms: number) => unknown;
  clearTimer: (t: unknown) => void;
  toast: (type: 'info' | 'success' | 'warning' | 'error', message: string, duration?: number) => void;
  newRequestId: () => string;
}

let seq = 0;
const defaultDeps: VestTpslDeps = {
  send: (cmd) => {
    if (!wsService.isConnected()) return false;
    wsService.emit(cmd.event, cmd.payload);
    return true;
  },
  now: () => Date.now(),
  setTimer: (fn, ms) => setTimeout(fn, ms),
  clearTimer: (t) => clearTimeout(t as ReturnType<typeof setTimeout>),
  toast: (type, message, duration) => { addToast(type, message, duration); },
  newRequestId: () => `chart-tpsl-${Date.now().toString(36)}-${(++seq).toString(36)}`,
};

let deps: VestTpslDeps = defaultDeps;
const timers = new Map<string, unknown>();

/** Tests only. */
export function setVestTpslDeps(next: Partial<VestTpslDeps> | null): void {
  deps = next ? { ...defaultDeps, ...next } : defaultDeps;
}

function legName(kind: VestLegKind): string {
  return kind === 'tp' ? 'TP' : 'SL';
}

function clearTimerFor(key: string): void {
  const t = timers.get(key);
  if (t !== undefined) deps.clearTimer(t);
  timers.delete(key);
}

export const useVestTpslStore = create<VestTpslState>((set, get) => {
  const put = (key: string, entry: VestLegEntry | null) => {
    const legs = { ...get().legs };
    if (entry) legs[key] = entry;
    else delete legs[key];
    set({ legs });
  };

  const inFlight = (key: string) => {
    const e = get().legs[key];
    return !!e && e.status === 'pending';
  };

  const send = (key: string, cmd: VestTpslCommand, entry: VestLegEntry, what: string) => {
    if (!deps.send(cmd)) {
      // Not sent: snap back (a draft stays a draft).
      put(key, entry.draftPrice !== undefined ? { status: 'draft', price: entry.draftPrice } : null);
      deps.toast('error', `${what}: not connected - nothing was sent`, 5000);
      return;
    }
    put(key, entry);
    clearTimerFor(key);
    timers.set(key, deps.setTimer(() => {
      timers.delete(key);
      const cur = get().legs[key];
      if (!cur || cur.request?.requestId !== entry.request?.requestId) return;
      put(key, null);
      if (!cur.confirmed) deps.toast('error', `${what}: no answer from Vest - check the position`, 6000);
    }, VEST_TPSL_REPLY_TIMEOUT_MS));
  };

  return {
    legs: {},

    dragTo: (row, kind, price) => {
      const key = vestLegKey(row, kind);
      if (inFlight(key)) return;
      const cur = get().legs[key];
      const draftPrice = cur?.status === 'draft' ? cur.price ?? undefined : cur?.draftPrice;
      put(key, { status: 'dragging', price, ...(draftPrice !== undefined ? { draftPrice } : {}) });
    },

    cancelDrag: (row, kind) => {
      const key = vestLegKey(row, kind);
      const cur = get().legs[key];
      if (!cur || cur.status !== 'dragging') return;
      put(key, cur.draftPrice !== undefined ? { status: 'draft', price: cur.draftPrice } : null);
    },

    addDraft: (row, kind, price) => {
      const key = vestLegKey(row, kind);
      if (get().legs[key]) return;
      put(key, { status: 'draft', price });
    },

    discardDraft: (row, kind) => {
      const key = vestLegKey(row, kind);
      const cur = get().legs[key];
      if (cur && (cur.status === 'draft' || (cur.status === 'dragging' && cur.draftPrice !== undefined))) put(key, null);
    },

    drop: (row, kind, rawPrice, ctx) => {
      const key = vestLegKey(row, kind);
      if (inFlight(key)) return;
      const cur = get().legs[key];
      const draftPrice = cur?.status === 'draft' ? cur.price ?? undefined : cur?.draftPrice;
      const snapBack = () => put(key, draftPrice !== undefined ? { status: 'draft', price: draftPrice } : null);
      const price = snapVestPrice(rawPrice, ctx.tick);
      const rowPrice = kind === 'tp' ? row.takeProfitPrice : row.stopLossPrice;
      if (draftPrice === undefined && rowPrice !== null && Math.abs(price - rowPrice) < 1e-12) {
        snapBack();
        return;
      }
      const why = checkVestLegPrice(row, kind, price, ctx.mark, ctx.decimals);
      if (why) {
        snapBack();
        deps.toast('warning', why, 4000);
        return;
      }
      const requestId = deps.newRequestId();
      const cmd = buildVestSetLegCommand(row, kind, price, requestId);
      if (!cmd) {
        snapBack();
        deps.toast('error', `${legName(kind)}: ${row.vestSymbol} cannot be changed from a chart`, 5000);
        return;
      }
      const request: VestPendingRequest = { key, brokerId: row.brokerId, op: 'set', requestId, sentAt: deps.now() };
      const verb = rowPrice === null ? 'Setting' : 'Moving';
      deps.toast('info', `${verb} ${legName(kind)}${rowPrice === null ? '' : ` ${rowPrice.toFixed(ctx.decimals)} ->`} ${price.toFixed(ctx.decimals)}`, 2500);
      send(key, cmd, { status: 'pending', price, request, ...(draftPrice !== undefined ? { draftPrice } : {}) }, `${legName(kind)} ${row.displaySymbol}`);
    },

    remove: (row, kind) => {
      const key = vestLegKey(row, kind);
      if (inFlight(key)) return;
      const requestId = deps.newRequestId();
      const cmd = buildVestRemoveLegCommand(row, kind, requestId);
      if (!cmd) {
        deps.toast('error', `${legName(kind)}: no working ${legName(kind)} id on this position yet - try again in a few seconds`, 5000);
        return;
      }
      const request: VestPendingRequest = {
        key, brokerId: row.brokerId, op: 'remove', requestId, orderId: String(cmd.payload.orderId), sentAt: deps.now(),
      };
      deps.toast('info', `Removing ${legName(kind)}...`, 2500);
      send(key, cmd, { status: 'pending', price: null, request }, `${legName(kind)} ${row.displaySymbol}`);
    },

    handleReply: (event, data) => {
      const pending = Object.values(get().legs)
        .filter(e => e.status === 'pending' && e.request && !e.confirmed)
        .map(e => e.request as VestPendingRequest);
      const hit = matchVestReply(pending, event, data);
      if (!hit) return;
      const { request, outcome } = hit;
      const cur = get().legs[request.key];
      if (!cur) return;
      const what = `${request.key.endsWith(':tp') ? 'TP' : 'SL'}`;
      if (outcome.kind === 'error') {
        clearTimerFor(request.key);
        put(request.key, cur.draftPrice !== undefined ? { status: 'draft', price: cur.draftPrice } : null);
        // Restrain refusals are toasted by restrainStore already.
        if (outcome.code === 'RESTRAIN_ACTIVE') return;
        // No fail slippage known for this market (the overlay never sent one
        // on this account): the chart has no per-market settings, so say where to set it.
        const parts = request.key.split(':');
        const market = parts[parts.length - 2] ?? '';
        const text = outcome.code === 'SLIPPAGE_REQUIRED'
          ? `set fail slippage for ${market} in the overlay`
          : outcome.message;
        deps.toast('error', `${what} not changed: ${text}`, 7000);
        return;
      }
      if (outcome.message) deps.toast('warning', `${what}: ${outcome.message}`, 7000);
      put(request.key, { ...cur, confirmed: true });
      clearTimerFor(request.key);
      timers.set(request.key, deps.setTimer(() => {
        timers.delete(request.key);
        const now = get().legs[request.key];
        if (now?.confirmed && now.request?.requestId === request.requestId) put(request.key, null);
      }, VEST_TPSL_CONFIRM_HOLD_MS));
    },

    onPositionsPush: (brokerId, rows) => {
      const legs = get().legs;
      const open = new Set(rows.flatMap(r => [vestLegKey(r, 'tp'), vestLegKey(r, 'sl')]));
      let changed = false;
      const next: Record<string, VestLegEntry> = {};
      for (const [key, e] of Object.entries(legs)) {
        const ofBroker = key.startsWith(`${brokerId}:`);
        if (ofBroker && (!open.has(key) || (e.confirmed && e.request?.brokerId === brokerId))) {
          clearTimerFor(key);
          changed = true;
          continue;
        }
        next[key] = e;
      }
      if (changed) set({ legs: next });
    },

    reset: () => {
      for (const key of [...timers.keys()]) clearTimerFor(key);
      set({ legs: {} });
    },
  };
});

let handlersRegistered = false;
let unsubscribePositions: (() => void) | null = null;

const onBracketReplaced = (d: unknown) => useVestTpslStore.getState().handleReply(VEST_BRACKET_REPLACED, d);
const onOrderCancelled = (d: unknown) => useVestTpslStore.getState().handleReply(VEST_ORDER_CANCELLED, d);
const onBrokerError = (d: unknown) => useVestTpslStore.getState().handleReply(VEST_BROKER_ERROR, d);

/** Same lifecycle as the positions handlers (WebSocketService init / forced reconnect). */
export function resetVestTpslSocketHandlers(): void {
  handlersRegistered = false;
  useVestTpslStore.getState().reset();
}

export function initVestTpslSocketHandlers(): void {
  if (handlersRegistered) return;
  handlersRegistered = true;
  wsService.on(VEST_BRACKET_REPLACED, onBracketReplaced);
  wsService.on(VEST_ORDER_CANCELLED, onOrderCancelled);
  wsService.on(VEST_BROKER_ERROR, onBrokerError);
  if (!unsubscribePositions) {
    // A new rows object per broker = a push that changed something for it.
    unsubscribePositions = useVestPositionsStore.subscribe((s, prev) => {
      if (s.byBroker === prev.byBroker) return;
      const brokers = new Set([...Object.keys(s.byBroker), ...Object.keys(prev.byBroker)]);
      for (const b of brokers) {
        if (s.byBroker[b] !== prev.byBroker[b]) useVestTpslStore.getState().onPositionsPush(b, s.byBroker[b] ?? []);
      }
    });
  }
}
